import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  CheckCircle as PostIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";

import {
  goodsReceiptsApi,
} from "../api/goodsReceiptsApi";

import {
  purchaseOrdersApi,
} from "../api/purchaseOrdersApi";

import {
  suppliersApi,
} from "../api/suppliersApi";

import {
  warehousesApi,
} from "../api/warehousesApi";

import type {
  CreateGoodsReceiptInput,
  GoodsReceiptLineInput,
  GoodsReceiptRecord,
  GoodsReceiptStatus,
} from "../types/goods-receipts.types";

import type {
  PurchaseOrderRecord,
} from "../types/purchase-orders.types";

import type {
  SupplierRecord,
} from "../types/suppliers.types";

import type {
  WarehouseRecord,
} from "../types/warehouses.types";

import "../screenstyles/GoodsReceipts.css";

type ReceiptFormLine = {
  purchaseOrderLineId: string;

  productId: string;

  productCode: string;

  productName: string;

  orderedQuantity: number;

  previouslyReceivedQuantity: number;

  remainingQuantity: number;

  quantity: number;

  unitPrice: number;

  notes: string;
};

type ReceiptForm = {
  purchaseOrder: PurchaseOrderRecord | null;

  receiptDate: string;

  referenceNumber: string;

  notes: string;

  items: ReceiptFormLine[];
};

const STATUS_LABELS: Record<
  GoodsReceiptStatus,
  string
> = {
  draft: "مسودة",
  posted: "مرحّل",
  cancelled: "ملغي",
};

function todayString(): string {
  const now = new Date();

  const year =
    now.getFullYear();

  const month =
    String(
      now.getMonth() + 1,
    ).padStart(2, "0");

  const day =
    String(
      now.getDate(),
    ).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function createEmptyForm(): ReceiptForm {
  return {
    purchaseOrder: null,

    receiptDate:
      todayString(),

    referenceNumber: "",

    notes: "",

    items: [],
  };
}

function toNumber(
  value: unknown,
): number {
  const parsed =
    Number(value);

  return Number.isFinite(parsed)
    ? parsed
    : 0;
}

function roundMoney(
  value: number,
): number {
  return (
    Math.round(
      (value + Number.EPSILON) *
        1000000,
    ) / 1000000
  );
}

function formatNumber(
  value: number,
): string {
  return new Intl.NumberFormat(
    "en-US",
    {
      maximumFractionDigits: 6,
    },
  ).format(value);
}

function getErrorMessage(
  error: unknown,
): string {
  if (
    typeof error === "object" &&
    error !== null
  ) {
    const candidate =
      error as {
        message?: unknown;

        response?: {
          data?: {
            message?: unknown;

            error?: {
              message?: unknown;
            };
          };
        };
      };

    const responseMessage =
      candidate.response?.data
        ?.error?.message ??
      candidate.response?.data
        ?.message;

    if (
      typeof responseMessage ===
      "string"
    ) {
      return responseMessage;
    }

    if (
      typeof candidate.message ===
      "string"
    ) {
      return candidate.message;
    }
  }

  return "حدث خطأ غير متوقع.";
}

function statusClass(
  status: GoodsReceiptStatus,
): string {
  return (
    `goods-receipts-status ` +
    `goods-receipts-status--${status}`
  );
}

function buildFormLines(
  order: PurchaseOrderRecord,
): ReceiptFormLine[] {
  return order.items
    .filter(
      item =>
        item.remainingQuantity > 0,
    )
    .map(
      item => ({
        purchaseOrderLineId:
          item.lineId,

        productId:
          item.productId,

        productCode:
          item.productCode,

        productName:
          item.productName,

        orderedQuantity:
          item.quantity,

        previouslyReceivedQuantity:
          item.receivedQuantity,

        remainingQuantity:
          item.remainingQuantity,

        quantity:
          item.remainingQuantity,

        unitPrice:
          item.unitPrice,

        notes: "",
      }),
    );
}

export default function GoodsReceipts() {
  const [
    receipts,
    setReceipts,
  ] = useState<
    GoodsReceiptRecord[]
  >([]);

  const [
    purchaseOrders,
    setPurchaseOrders,
  ] = useState<
    PurchaseOrderRecord[]
  >([]);

  const [
    suppliers,
    setSuppliers,
  ] = useState<
    SupplierRecord[]
  >([]);

  const [
    warehouses,
    setWarehouses,
  ] = useState<
    WarehouseRecord[]
  >([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    statusFilter,
    setStatusFilter,
  ] = useState<
    "" | GoodsReceiptStatus
  >("");

  const [
    supplierFilter,
    setSupplierFilter,
  ] = useState("");

  const [
    warehouseFilter,
    setWarehouseFilter,
  ] = useState("");

  const [
    fromDate,
    setFromDate,
  ] = useState("");

  const [
    toDate,
    setToDate,
  ] = useState("");

  const [
    formOpen,
    setFormOpen,
  ] = useState(false);

  const [
    detailsOpen,
    setDetailsOpen,
  ] = useState(false);

  const [
    selectedReceipt,
    setSelectedReceipt,
  ] = useState<
    GoodsReceiptRecord | null
  >(null);

  const [
    editingReceipt,
    setEditingReceipt,
  ] = useState<
    GoodsReceiptRecord | null
  >(null);

  const [
    form,
    setForm,
  ] = useState<
    ReceiptForm
  >(
    createEmptyForm(),
  );

  const [
    postDialogOpen,
    setPostDialogOpen,
  ] = useState(false);

  const [
    cancelDialogOpen,
    setCancelDialogOpen,
  ] = useState(false);

  const [
    actionReceipt,
    setActionReceipt,
  ] = useState<
    GoodsReceiptRecord | null
  >(null);

  const [
    actionLoading,
    setActionLoading,
  ] = useState(false);

  const [
    actionError,
    setActionError,
  ] = useState("");

  const [
    cancelReason,
    setCancelReason,
  ] = useState("");

  const loadReferenceData =
    useCallback(
      async () => {
        const [
          approvedOrders,
          partialOrders,
          suppliersResult,
          warehousesResult,
        ] =
          await Promise.all([
            purchaseOrdersApi.list({
              status: "approved",
              limit: 100,
              sort:
                "orderDate_desc",
            }),

            purchaseOrdersApi.list({
              status:
                "partially_received",
              limit: 100,
              sort:
                "orderDate_desc",
            }),

            suppliersApi.list({
              isActive: true,
              limit: 100,
              sort: "name_asc",
            }),

            warehousesApi.list({
              isActive: true,
              limit: 100,
              sort: "name_asc",
            }),
          ]);

        const allOrders = [
          ...approvedOrders.items,
          ...partialOrders.items,
        ];

        const uniqueOrders =
          Array.from(
            new Map(
              allOrders.map(
                order => [
                  order.purchaseOrderId,
                  order,
                ],
              ),
            ).values(),
          );

        setPurchaseOrders(
          uniqueOrders,
        );

        setSuppliers(
          suppliersResult.items,
        );

        setWarehouses(
          warehousesResult.items,
        );
      },
      [],
    );

  const loadReceipts =
    useCallback(
      async () => {
        setLoading(true);

        setError("");

        try {
          const result =
            await goodsReceiptsApi.list({
              search:
                search.trim() ||
                undefined,

              status:
                statusFilter ||
                undefined,

              supplierId:
                supplierFilter ||
                undefined,

              warehouseId:
                warehouseFilter ||
                undefined,

              fromDate:
                fromDate ||
                undefined,

              toDate:
                toDate ||
                undefined,

              sort:
                "receiptDate_desc",

              limit: 100,
            });

          setReceipts(
            result.items,
          );
        } catch (
          loadError
        ) {
          setError(
            getErrorMessage(
              loadError,
            ),
          );
        } finally {
          setLoading(false);
        }
      },
      [
        search,
        statusFilter,
        supplierFilter,
        warehouseFilter,
        fromDate,
        toDate,
      ],
    );

  useEffect(
    () => {
      void loadReferenceData()
        .catch(
          referenceError => {
            setError(
              getErrorMessage(
                referenceError,
              ),
            );
          },
        );
    },
    [
      loadReferenceData,
    ],
  );

  useEffect(
    () => {
      const timer =
        window.setTimeout(
          () => {
            void loadReceipts();
          },
          250,
        );

      return () =>
        window.clearTimeout(
          timer,
        );
    },
    [
      loadReceipts,
    ],
  );

  const calculatedTotals =
    useMemo(
      () => {
        const totalQuantity =
          form.items.reduce(
            (
              total,
              item,
            ) =>
              total +
              toNumber(
                item.quantity,
              ),
            0,
          );

        const totalValue =
          roundMoney(
            form.items.reduce(
              (
                total,
                item,
              ) =>
                total +
                (
                  toNumber(
                    item.quantity,
                  ) *
                  toNumber(
                    item.unitPrice,
                  )
                ),
              0,
            ),
          );

        const exchangeRate =
          form.purchaseOrder
            ?.exchangeRate ??
          1;

        return {
          totalQuantity,

          totalValue,

          baseTotalValue:
            roundMoney(
              totalValue *
                exchangeRate,
            ),
        };
      },
      [
        form.items,
        form.purchaseOrder,
      ],
    );

  function openCreateDialog() {
    setEditingReceipt(null);

    setForm(
      createEmptyForm(),
    );

    setError("");

    setFormOpen(true);
  }

  function openEditDialog(
    receipt:
      GoodsReceiptRecord,
  ) {
    const order =
      purchaseOrders.find(
        item =>
          item.purchaseOrderId ===
          receipt.purchaseOrderId,
      ) ?? null;

    const lines:
      ReceiptFormLine[] =
        receipt.items.map(
          item => ({
            purchaseOrderLineId:
              item.purchaseOrderLineId,

            productId:
              item.productId,

            productCode:
              item.productCode,

            productName:
              item.productName,

            orderedQuantity:
              item.orderedQuantity,

            previouslyReceivedQuantity:
              item.previouslyReceivedQuantity,

            remainingQuantity:
              item.receivedQuantity +
              item.remainingQuantityAfterReceipt,

            quantity:
              item.receivedQuantity,

            unitPrice:
              item.unitPrice,

            notes:
              item.notes ??
              "",
          }),
        );

    setEditingReceipt(
      receipt,
    );

    setForm({
      purchaseOrder:
        order,

      receiptDate:
        receipt.receiptDate,

      referenceNumber:
        receipt.referenceNumber ??
        "",

      notes:
        receipt.notes ??
        "",

      items:
        lines,
    });

    setError("");

    setFormOpen(true);
  }

  function selectPurchaseOrder(
    order:
      PurchaseOrderRecord | null,
  ) {
    setForm(
      current => ({
        ...current,

        purchaseOrder:
          order,

        items:
          order
            ? buildFormLines(
                order,
              )
            : [],
      }),
    );
  }

  function updateLine(
    index: number,

    patch:
      Partial<ReceiptFormLine>,
  ) {
    setForm(
      current => ({
        ...current,

        items:
          current.items.map(
            (
              item,
              itemIndex,
            ) =>
              itemIndex ===
              index
                ? {
                    ...item,
                    ...patch,
                  }
                : item,
          ),
      }),
    );
  }

  function validateForm():
    string | null {
    if (
      !form.purchaseOrder
    ) {
      return "يرجى اختيار أمر الشراء.";
    }

    if (
      !form.receiptDate
    ) {
      return "يرجى تحديد تاريخ الاستلام.";
    }

    if (
      form.items.length === 0
    ) {
      return "لا توجد مواد متبقية للاستلام في أمر الشراء.";
    }

    const activeLines =
      form.items.filter(
        item =>
          toNumber(
            item.quantity,
          ) > 0,
      );

    if (
      activeLines.length ===
      0
    ) {
      return "يجب إدخال كمية استلام لمادة واحدة على الأقل.";
    }

    for (
      const item of
      activeLines
    ) {
      const quantity =
        toNumber(
          item.quantity,
        );

      if (
        !Number.isInteger(
          quantity,
        )
      ) {
        return `كمية الاستلام للمادة ${item.productName} يجب أن تكون عدداً صحيحاً.`;
      }

      if (
        quantity >
        item.remainingQuantity
      ) {
        return `كمية الاستلام للمادة ${item.productName} أكبر من الكمية المتبقية.`;
      }
    }

    return null;
  }

  function buildItems():
    GoodsReceiptLineInput[] {
    return form.items
      .filter(
        item =>
          toNumber(
            item.quantity,
          ) > 0,
      )
      .map(
        item => ({
          purchaseOrderLineId:
            item.purchaseOrderLineId,

          quantity:
            toNumber(
              item.quantity,
            ),

          notes:
            item.notes.trim() ||
            null,
        }),
      );
  }

  async function saveReceipt() {
    const validationError =
      validateForm();

    if (
      validationError
    ) {
      setError(
        validationError,
      );

      return;
    }

    setSaving(true);

    setError("");

    try {
      const input:
        CreateGoodsReceiptInput = {
        purchaseOrderId:
          form.purchaseOrder!
            .purchaseOrderId,

        receiptDate:
          form.receiptDate,

        referenceNumber:
          form.referenceNumber
            .trim() ||
          null,

        notes:
          form.notes.trim() ||
          null,

        items:
          buildItems(),
      };

      if (
        editingReceipt
      ) {
        await goodsReceiptsApi.update(
          editingReceipt
            .goodsReceiptId,
          {
            receiptDate:
              input.receiptDate,

            referenceNumber:
              input.referenceNumber,

            notes:
              input.notes,

            items:
              input.items,
          },
        );
      } else {
        await goodsReceiptsApi.create(
          input,
        );
      }

      setFormOpen(false);

      setEditingReceipt(
        null,
      );

      await Promise.all([
        loadReceipts(),
        loadReferenceData(),
      ]);
    } catch (
      saveError
    ) {
      setError(
        getErrorMessage(
          saveError,
        ),
      );
    } finally {
      setSaving(false);
    }
  }

  async function viewReceipt(
    receipt:
      GoodsReceiptRecord,
  ) {
    setError("");

    try {
      const result =
        await goodsReceiptsApi.getById(
          receipt.goodsReceiptId,
        );

      setSelectedReceipt(
        result.goodsReceipt,
      );

      setDetailsOpen(true);
    } catch (
      detailsError
    ) {
      setError(
        getErrorMessage(
          detailsError,
        ),
      );
    }
  }

  function openPostDialog(
    receipt:
      GoodsReceiptRecord,
  ) {
    setActionReceipt(
      receipt,
    );

    setActionError("");

    setPostDialogOpen(true);
  }

  async function confirmPostReceipt() {
    if (
      !actionReceipt
    ) {
      return;
    }

    setActionLoading(true);

    setActionError("");

    try {
      await goodsReceiptsApi.post(
        actionReceipt
          .goodsReceiptId,
      );

      setPostDialogOpen(
        false,
      );

      setActionReceipt(
        null,
      );

      await Promise.all([
        loadReceipts(),
        loadReferenceData(),
      ]);
    } catch (
      postError
    ) {
      setActionError(
        getErrorMessage(
          postError,
        ),
      );
    } finally {
      setActionLoading(false);
    }
  }

  function openCancelDialog(
    receipt:
      GoodsReceiptRecord,
  ) {
    setActionReceipt(
      receipt,
    );

    setCancelReason("");

    setActionError("");

    setCancelDialogOpen(
      true,
    );
  }

  async function confirmCancelReceipt() {
    if (
      !actionReceipt
    ) {
      return;
    }

    const reason =
      cancelReason.trim();

    if (!reason) {
      setActionError(
        "سبب الإلغاء مطلوب.",
      );

      return;
    }

    setActionLoading(true);

    setActionError("");

    try {
      await goodsReceiptsApi.cancel(
        actionReceipt
          .goodsReceiptId,
        {
          reason,
        },
      );

      setCancelDialogOpen(
        false,
      );

      setActionReceipt(
        null,
      );

      setCancelReason("");

      await Promise.all([
        loadReceipts(),
        loadReferenceData(),
      ]);
    } catch (
      cancelError
    ) {
      setActionError(
        getErrorMessage(
          cancelError,
        ),
      );
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <Box
      className="goods-receipts-page"
      dir="rtl"
    >
      <Box className="goods-receipts-header">
        <Box>
          <Typography
            variant="h4"
            className="goods-receipts-title"
          >
            استلام البضاعة
          </Typography>

          <Typography className="goods-receipts-subtitle">
            تسجيل استلام المواد من الموردين وربطها بأوامر الشراء وتحديث المخزون
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1}
        >
          <Tooltip title="تحديث">
            <IconButton
              onClick={() =>
                void Promise.all([
                  loadReceipts(),
                  loadReferenceData(),
                ])
              }
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={
              <AddIcon />
            }
            onClick={
              openCreateDialog
            }
          >
            إضافة استلام
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert
          severity="error"
          onClose={() =>
            setError("")
          }
          sx={{
            mb: 2,
          }}
        >
          {error}
        </Alert>
      )}

      <Paper className="goods-receipts-filters">
        <TextField
          label="بحث"
          size="small"
          value={search}
          onChange={
            event =>
              setSearch(
                event.target.value,
              )
          }
          placeholder="رقم الاستلام أو أمر الشراء أو المورد"
        />

        <TextField
          select
          label="الحالة"
          size="small"
          value={
            statusFilter
          }
          onChange={
            event =>
              setStatusFilter(
                event.target.value as
                  | ""
                  | GoodsReceiptStatus,
              )
          }
        >
          <MenuItem value="">
            جميع الحالات
          </MenuItem>

          {(
            Object.keys(
              STATUS_LABELS,
            ) as GoodsReceiptStatus[]
          ).map(
            status => (
              <MenuItem
                key={status}
                value={status}
              >
                {
                  STATUS_LABELS[
                    status
                  ]
                }
              </MenuItem>
            ),
          )}
        </TextField>

        <TextField
          select
          label="المورد"
          size="small"
          value={
            supplierFilter
          }
          onChange={
            event =>
              setSupplierFilter(
                event.target.value,
              )
          }
        >
          <MenuItem value="">
            جميع الموردين
          </MenuItem>

          {suppliers.map(
            supplier => (
              <MenuItem
                key={
                  supplier.id
                }
                value={
                  supplier.id
                }
              >
                {supplier.nameAr ||
                  supplier.name}
              </MenuItem>
            ),
          )}
        </TextField>

        <TextField
          select
          label="المخزن"
          size="small"
          value={
            warehouseFilter
          }
          onChange={
            event =>
              setWarehouseFilter(
                event.target.value,
              )
          }
        >
          <MenuItem value="">
            جميع المخازن
          </MenuItem>

          {warehouses.map(
            warehouse => (
              <MenuItem
                key={
                  warehouse.id
                }
                value={
                  warehouse.id
                }
              >
                {warehouse.nameAr ||
                  warehouse.name}
              </MenuItem>
            ),
          )}
        </TextField>

        <TextField
          label="من تاريخ"
          type="date"
          size="small"
          value={
            fromDate
          }
          onChange={
            event =>
              setFromDate(
                event.target.value,
              )
          }
          InputLabelProps={{
            shrink: true,
          }}
        />

        <TextField
          label="إلى تاريخ"
          type="date"
          size="small"
          value={
            toDate
          }
          onChange={
            event =>
              setToDate(
                event.target.value,
              )
          }
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Paper>

      <TableContainer
        component={Paper}
        className="goods-receipts-table-container"
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                رقم الاستلام
              </TableCell>

              <TableCell>
                التاريخ
              </TableCell>

              <TableCell>
                أمر الشراء
              </TableCell>

              <TableCell>
                المورد
              </TableCell>

              <TableCell>
                المخزن
              </TableCell>

              <TableCell>
                إجمالي الكمية
              </TableCell>

              <TableCell>
                القيمة
              </TableCell>

              <TableCell>
                الحالة
              </TableCell>

              <TableCell align="center">
                الإجراءات
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  align="center"
                >
                  <Box sx={{ py: 5 }}>
                    <CircularProgress
                      size={30}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ) : receipts.length ===
              0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  align="center"
                >
                  <Box sx={{ py: 5 }}>
                    لا توجد سندات استلام
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              receipts.map(
                receipt => (
                  <TableRow
                    key={
                      receipt.goodsReceiptId
                    }
                    hover
                  >
                    <TableCell>
                      <strong>
                        {
                          receipt.receiptNumber
                        }
                      </strong>
                    </TableCell>

                    <TableCell>
                      {
                        receipt.receiptDate
                      }
                    </TableCell>

                    <TableCell>
                      {
                        receipt.purchaseOrderNumber
                      }
                    </TableCell>

                    <TableCell>
                      {receipt.supplierNameAr ||
                        receipt.supplierName}
                    </TableCell>

                    <TableCell>
                      {receipt.warehouseNameAr ||
                        receipt.warehouseName}
                    </TableCell>

                    <TableCell>
                      {
                        receipt.totalQuantity
                      }
                    </TableCell>

                    <TableCell>
                      {formatNumber(
                        receipt.totalValue,
                      )}{" "}
                      {
                        receipt.currencyCode
                      }
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          STATUS_LABELS[
                            receipt.status
                          ]
                        }
                        className={
                          statusClass(
                            receipt.status,
                          )
                        }
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="center"
                      >
                        <Tooltip title="عرض">
                          <IconButton
                            size="small"
                            onClick={() =>
                              void viewReceipt(
                                receipt,
                              )
                            }
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {receipt.status ===
                          "draft" && (
                          <>
                            <Tooltip title="تعديل">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  openEditDialog(
                                    receipt,
                                  )
                                }
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="ترحيل الاستلام">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  openPostDialog(
                                    receipt,
                                  )
                                }
                              >
                                <PostIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="إلغاء">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  openCancelDialog(
                                    receipt,
                                  )
                                }
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ),
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={formOpen}
        onClose={() => {
          if (!saving) {
            setFormOpen(false);
          }
        }}
        className="goods-receipts-dialog"
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <span>
              {editingReceipt
                ? `تعديل استلام ${editingReceipt.receiptNumber}`
                : "إضافة استلام بضاعة"}
            </span>

            <IconButton
              onClick={() =>
                setFormOpen(false)
              }
              disabled={saving}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Box className="goods-receipts-form-grid">
            <Autocomplete
              options={
                purchaseOrders
              }
              value={
                form.purchaseOrder
              }
              disabled={
                Boolean(
                  editingReceipt,
                )
              }
              isOptionEqualToValue={(
                option,
                value,
              ) =>
                option.purchaseOrderId ===
                value.purchaseOrderId
              }
              getOptionLabel={
                option =>
                  `${option.orderNumber} - ${
                    option.supplierNameAr ||
                    option.supplierName
                  }`
              }
              onChange={(
                _,
                value,
              ) =>
                selectPurchaseOrder(
                  value,
                )
              }
              renderInput={
                params => (
                  <TextField
                    {...params}
                    label="أمر الشراء *"
                  />
                )
              }
            />

            <TextField
              label="تاريخ الاستلام *"
              type="date"
              value={
                form.receiptDate
              }
              onChange={
                event =>
                  setForm(
                    current => ({
                      ...current,

                      receiptDate:
                        event.target.value,
                    }),
                  )
              }
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              label="الرقم المرجعي"
              value={
                form.referenceNumber
              }
              onChange={
                event =>
                  setForm(
                    current => ({
                      ...current,

                      referenceNumber:
                        event.target.value,
                    }),
                  )
              }
            />

            <TextField
              label="المورد"
              value={
                form.purchaseOrder
                  ? (
                      form.purchaseOrder
                        .supplierNameAr ||
                      form.purchaseOrder
                        .supplierName
                    )
                  : ""
              }
              disabled
            />

            <TextField
              label="المخزن"
              value={
                form.purchaseOrder
                  ? (
                      form.purchaseOrder
                        .warehouseNameAr ||
                      form.purchaseOrder
                        .warehouseName
                    )
                  : ""
              }
              disabled
            />

            <TextField
              label="العملة"
              value={
                form.purchaseOrder
                  ?.currencyCode ??
                ""
              }
              disabled
            />

            <TextField
              label="ملاحظات"
              value={
                form.notes
              }
              onChange={
                event =>
                  setForm(
                    current => ({
                      ...current,

                      notes:
                        event.target.value,
                    }),
                  )
              }
              multiline
              minRows={2}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box className="goods-receipts-items-header">
            <Typography variant="h6">
              مواد الاستلام
            </Typography>

            {form.purchaseOrder && (
              <Chip
                label={`أمر الشراء: ${form.purchaseOrder.orderNumber}`}
                variant="outlined"
              />
            )}
          </Box>

          <TableContainer className="goods-receipts-lines-table">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    الكود
                  </TableCell>

                  <TableCell>
                    المادة
                  </TableCell>

                  <TableCell>
                    المطلوبة
                  </TableCell>

                  <TableCell>
                    المستلمة سابقاً
                  </TableCell>

                  <TableCell>
                    المتبقية
                  </TableCell>

                  <TableCell>
                    استلام الآن
                  </TableCell>

                  <TableCell>
                    سعر الشراء
                  </TableCell>

                  <TableCell>
                    القيمة
                  </TableCell>

                  <TableCell>
                    ملاحظات
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {form.items.length ===
                0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      align="center"
                    >
                      اختر أمر شراء لعرض المواد المتبقية للاستلام.
                    </TableCell>
                  </TableRow>
                ) : (
                  form.items.map(
                    (
                      item,
                      index,
                    ) => (
                      <TableRow
                        key={
                          item.purchaseOrderLineId
                        }
                      >
                        <TableCell>
                          {
                            item.productCode
                          }
                        </TableCell>

                        <TableCell>
                          <strong>
                            {
                              item.productName
                            }
                          </strong>
                        </TableCell>

                        <TableCell>
                          {
                            item.orderedQuantity
                          }
                        </TableCell>

                        <TableCell>
                          {
                            item.previouslyReceivedQuantity
                          }
                        </TableCell>

                        <TableCell>
                          {
                            item.remainingQuantity
                          }
                        </TableCell>

                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={
                              item.quantity
                            }
                            onChange={
                              event =>
                                updateLine(
                                  index,
                                  {
                                    quantity:
                                      toNumber(
                                        event.target.value,
                                      ),
                                  },
                                )
                            }
                            inputProps={{
                              min: 0,
                              max:
                                item.remainingQuantity,
                              step: 1,
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          {formatNumber(
                            item.unitPrice,
                          )}
                        </TableCell>

                        <TableCell>
                          <strong>
                            {formatNumber(
                              roundMoney(
                                toNumber(
                                  item.quantity,
                                ) *
                                  item.unitPrice,
                              ),
                            )}
                          </strong>
                        </TableCell>

                        <TableCell>
                          <TextField
                            size="small"
                            value={
                              item.notes
                            }
                            onChange={
                              event =>
                                updateLine(
                                  index,
                                  {
                                    notes:
                                      event.target.value,
                                  },
                                )
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ),
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box className="goods-receipts-totals">
            <Box>
              <span>
                إجمالي الكمية
              </span>

              <strong>
                {
                  calculatedTotals.totalQuantity
                }
              </strong>
            </Box>

            <Box className="goods-receipts-grand-total">
              <span>
                إجمالي قيمة الاستلام
              </span>

              <strong>
                {formatNumber(
                  calculatedTotals.totalValue,
                )}{" "}
                {
                  form.purchaseOrder
                    ?.currencyCode ??
                  ""
                }
              </strong>
            </Box>

            {form.purchaseOrder &&
              form.purchaseOrder
                .currencyCode !==
                form.purchaseOrder
                  .baseCurrencyCode && (
                <Box>
                  <span>
                    القيمة بالعملة الأساسية
                  </span>

                  <strong>
                    {formatNumber(
                      calculatedTotals
                        .baseTotalValue,
                    )}{" "}
                    {
                      form.purchaseOrder
                        .baseCurrencyCode
                    }
                  </strong>
                </Box>
              )}
          </Box>

          <Alert
            severity="info"
            sx={{ mt: 3 }}
          >
            حفظ سند الاستلام كمسودة لا يغيّر المخزون. تحديث المخزون يحدث فقط عند ترحيل الاستلام.
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() =>
              setFormOpen(false)
            }
            disabled={saving}
          >
            إلغاء
          </Button>

          <Button
            variant="contained"
            disabled={saving}
            onClick={() =>
              void saveReceipt()
            }
          >
            {saving
              ? "جاري الحفظ..."
              : editingReceipt
                ? "حفظ التعديلات"
                : "إنشاء سند الاستلام"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailsOpen}
        onClose={() =>
          setDetailsOpen(false)
        }
        className="goods-receipts-dialog"
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <span>
              تفاصيل استلام البضاعة
              {selectedReceipt
                ? ` - ${selectedReceipt.receiptNumber}`
                : ""}
            </span>

            <IconButton
              onClick={() =>
                setDetailsOpen(false)
              }
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {selectedReceipt && (
            <>
              <Box className="goods-receipts-details-grid">
                <Box>
                  <span>
                    رقم الاستلام
                  </span>

                  <strong>
                    {
                      selectedReceipt.receiptNumber
                    }
                  </strong>
                </Box>

                <Box>
                  <span>
                    أمر الشراء
                  </span>

                  <strong>
                    {
                      selectedReceipt.purchaseOrderNumber
                    }
                  </strong>
                </Box>

                <Box>
                  <span>
                    المورد
                  </span>

                  <strong>
                    {selectedReceipt.supplierNameAr ||
                      selectedReceipt.supplierName}
                  </strong>
                </Box>

                <Box>
                  <span>
                    المخزن
                  </span>

                  <strong>
                    {selectedReceipt.warehouseNameAr ||
                      selectedReceipt.warehouseName}
                  </strong>
                </Box>

                <Box>
                  <span>
                    تاريخ الاستلام
                  </span>

                  <strong>
                    {
                      selectedReceipt.receiptDate
                    }
                  </strong>
                </Box>

                <Box>
                  <span>
                    العملة
                  </span>

                  <strong>
                    {
                      selectedReceipt.currencyCode
                    }
                  </strong>
                </Box>

                <Box>
                  <span>
                    الحالة
                  </span>

                  <Chip
                    size="small"
                    label={
                      STATUS_LABELS[
                        selectedReceipt.status
                      ]
                    }
                    className={
                      statusClass(
                        selectedReceipt.status,
                      )
                    }
                  />
                </Box>

                <Box>
                  <span>
                    الرقم المرجعي
                  </span>

                  <strong>
                    {selectedReceipt.referenceNumber ||
                      "-"}
                  </strong>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <TableContainer className="goods-receipts-lines-table">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        الكود
                      </TableCell>

                      <TableCell>
                        المادة
                      </TableCell>

                      <TableCell>
                        المطلوبة
                      </TableCell>

                      <TableCell>
                        المستلم سابقاً
                      </TableCell>

                      <TableCell>
                        المستلم بهذا السند
                      </TableCell>

                      <TableCell>
                        المتبقي بعد الاستلام
                      </TableCell>

                      <TableCell>
                        السعر
                      </TableCell>

                      <TableCell>
                        القيمة
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {selectedReceipt.items.map(
                      item => (
                        <TableRow
                          key={
                            item.lineId
                          }
                        >
                          <TableCell>
                            {
                              item.productCode
                            }
                          </TableCell>

                          <TableCell>
                            {
                              item.productName
                            }
                          </TableCell>

                          <TableCell>
                            {
                              item.orderedQuantity
                            }
                          </TableCell>

                          <TableCell>
                            {
                              item.previouslyReceivedQuantity
                            }
                          </TableCell>

                          <TableCell>
                            {
                              item.receivedQuantity
                            }
                          </TableCell>

                          <TableCell>
                            {
                              item.remainingQuantityAfterReceipt
                            }
                          </TableCell>

                          <TableCell>
                            {formatNumber(
                              item.unitPrice,
                            )}
                          </TableCell>

                          <TableCell>
                            {formatNumber(
                              item.lineValue,
                            )}
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box className="goods-receipts-totals goods-receipts-details-totals">
                <Box>
                  <span>
                    إجمالي الكمية
                  </span>

                  <strong>
                    {
                      selectedReceipt.totalQuantity
                    }
                  </strong>
                </Box>

                <Box className="goods-receipts-grand-total">
                  <span>
                    إجمالي القيمة
                  </span>

                  <strong>
                    {formatNumber(
                      selectedReceipt.totalValue,
                    )}{" "}
                    {
                      selectedReceipt.currencyCode
                    }
                  </strong>
                </Box>
              </Box>

              {selectedReceipt.notes && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2">
                    الملاحظات
                  </Typography>

                  <Typography>
                    {
                      selectedReceipt.notes
                    }
                  </Typography>
                </Box>
              )}

              {selectedReceipt.cancellationReason && (
                <Alert
                  severity="warning"
                  sx={{ mt: 3 }}
                >
                  سبب الإلغاء:{" "}
                  {
                    selectedReceipt.cancellationReason
                  }
                </Alert>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={postDialogOpen}
        onClose={() => {
          if (
            !actionLoading
          ) {
            setPostDialogOpen(
              false,
            );

            setActionReceipt(
              null,
            );

            setActionError("");
          }
        }}
        className="goods-receipts-dialog goods-receipts-action-dialog"
        fullWidth
        maxWidth="sm"
        dir="rtl"
      >
        <DialogTitle>
          ترحيل استلام البضاعة
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography>
              هل أنت متأكد من ترحيل سند الاستلام{" "}
              <strong>
                {
                  actionReceipt?.receiptNumber
                }
              </strong>
              ؟
            </Typography>

            <Alert severity="warning">
              الترحيل سيزيد مخزون المستودع ويحدّث الكميات المستلمة والمتبقية في أمر الشراء، ولا يمكن إلغاء السند المرحّل مباشرة.
            </Alert>

            {actionError && (
              <Alert severity="error">
                {
                  actionError
                }
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setPostDialogOpen(
                false,
              );

              setActionReceipt(
                null,
              );

              setActionError("");
            }}
            disabled={
              actionLoading
            }
          >
            تراجع
          </Button>

          <Button
            variant="contained"
            startIcon={
              actionLoading
                ? (
                    <CircularProgress
                      size={18}
                      color="inherit"
                    />
                  )
                : <PostIcon />
            }
            onClick={() =>
              void confirmPostReceipt()
            }
            disabled={
              actionLoading
            }
          >
            {actionLoading
              ? "جاري الترحيل..."
              : "تأكيد الترحيل"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={
          cancelDialogOpen
        }
        onClose={() => {
          if (
            !actionLoading
          ) {
            setCancelDialogOpen(
              false,
            );

            setActionReceipt(
              null,
            );

            setCancelReason("");

            setActionError("");
          }
        }}
        className="goods-receipts-dialog goods-receipts-action-dialog"
        fullWidth
        maxWidth="sm"
        dir="rtl"
      >
        <DialogTitle>
          إلغاء سند الاستلام
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography>
              أنت على وشك إلغاء سند الاستلام{" "}
              <strong>
                {
                  actionReceipt?.receiptNumber
                }
              </strong>
            </Typography>

            <Alert severity="warning">
              يمكن إلغاء سند الاستلام فقط عندما يكون في حالة مسودة.
            </Alert>

            <TextField
              label="سبب الإلغاء *"
              value={
                cancelReason
              }
              onChange={
                event => {
                  setCancelReason(
                    event.target.value,
                  );

                  if (
                    actionError
                  ) {
                    setActionError(
                      "",
                    );
                  }
                }
              }
              multiline
              minRows={3}
              fullWidth
              autoFocus
              disabled={
                actionLoading
              }
            />

            {actionError && (
              <Alert severity="error">
                {
                  actionError
                }
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setCancelDialogOpen(
                false,
              );

              setActionReceipt(
                null,
              );

              setCancelReason("");

              setActionError("");
            }}
            disabled={
              actionLoading
            }
          >
            تراجع
          </Button>

          <Button
            variant="contained"
            color="error"
            startIcon={
              actionLoading
                ? (
                    <CircularProgress
                      size={18}
                      color="inherit"
                    />
                  )
                : <CancelIcon />
            }
            onClick={() =>
              void confirmCancelReceipt()
            }
            disabled={
              actionLoading
            }
          >
            {actionLoading
              ? "جاري الإلغاء..."
              : "تأكيد الإلغاء"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}